import { PrototypeApiError } from './apiTransport';
import { renderAiSubmissionDetail } from './aiSubmissionDetailRenderer';
import type { AiJobDetail } from './aiJobDetailController';
import { closeModal, handoffModalForNavigation, openModal } from './navigationController';
import type { Root } from './prototypeTypes';
import { getRuntime } from './runtimeAccessor';

/**
 * Reader dialog for an AI Create submission, opened from the icon on a
 * completed topic card. Interaction mirrors communityIdeaDialogController
 * (open/abort/retry, Back-closes-modal, focus return) but stays independent
 * of Community return-state: the dialog lives on the AI Create surface.
 */

interface DialogRequest {
	generation: number;
	jobId: string;
	controller: AbortController;
}

const requests = new WeakMap<object, DialogRequest>();
const dialogTitleId = 'aiSubmissionDetailDialogTitle';

function dialog(root: Root): HTMLElement | null { return root.querySelector<HTMLElement>('#aiSubmissionDetailModal'); }
function host(root: Root): HTMLElement | null { return dialog(root)?.querySelector<HTMLElement>('[data-ai-submission-detail-host]') ?? null; }

function labelDialog(root: Root): void {
	const modal = dialog(root)?.querySelector<HTMLElement>('.modal');
	if (!modal) return;
	modal.setAttribute('aria-labelledby', dialogTitleId);
	modal.removeAttribute('aria-label');
}

function paintState(root: Root, title: string, message: string, jobId?: string): void {
	const target = host(root);
	if (!target) return;
	target.innerHTML = `<section class="community-detail-state" role="status"><h2 id="${dialogTitleId}" class="community-detail-title">${title}</h2><p>${message}</p>${jobId ? `<button type="button" class="btn btn-secondary" data-proto-click="retryAiSubmissionDialog('${jobId}')" aria-label="Retry loading submission">Retry</button>` : ''}</section>`;
	labelDialog(root);
}

/**
 * Reserve the reader's information architecture without inventing content —
 * same deliberate blank slots as the Community proposal dialog.
 */
function paintLoadingSlots(root: Root): void {
	const target = host(root);
	if (!target) return;
	target.innerHTML = `<article class="community-detail community-detail--loading" aria-busy="true">
		<h2 id="${dialogTitleId}" class="community-detail-title" aria-label="Submission details"><span class="community-detail-loading-slot community-detail-loading-slot--title" aria-hidden="true"></span></h2>
		<p class="community-detail-subtitle"><span class="community-detail-loading-slot community-detail-loading-slot--subtitle" aria-hidden="true"></span></p>
		<div class="community-detail-status-row" aria-hidden="true"><span class="community-detail-loading-slot community-detail-loading-slot--status"></span><span class="community-detail-loading-slot community-detail-loading-slot--vote"></span></div>
		<section class="community-detail-section" aria-hidden="true"><h3><span class="community-detail-loading-slot community-detail-loading-slot--heading"></span></h3><span class="community-detail-loading-slot community-detail-loading-slot--list"></span></section>
		<section class="community-detail-section" aria-hidden="true"><h3><span class="community-detail-loading-slot community-detail-loading-slot--heading"></span></h3><span class="community-detail-loading-slot community-detail-loading-slot--list-compact"></span></section>
		<section class="community-detail-section" aria-hidden="true"><h3><span class="community-detail-loading-slot community-detail-loading-slot--heading"></span></h3><span class="community-detail-loading-slot community-detail-loading-slot--resource"></span></section>
	</article>`;
	labelDialog(root);
}

function isCurrent(root: Root, request: DialogRequest): boolean {
	return requests.get(root as object) === request && !request.controller.signal.aborted && dialog(root)?.classList.contains('active') === true;
}

function startRequest(root: Root, jobId: string): DialogRequest {
	const previous = requests.get(root as object);
	previous?.controller.abort();
	const request: DialogRequest = {
		generation: (previous?.generation ?? 0) + 1,
		jobId,
		controller: new AbortController(),
	};
	requests.set(root as object, request);
	paintLoadingSlots(root);
	void loadAiSubmissionDialog(root, request);
	return request;
}

/** Opens the reader dialog without leaving the AI Create view. */
export function openAiSubmissionDialog(root: Root, jobId: string, returnFocus?: HTMLElement | null): void {
	openModal(root, 'aiSubmissionDetailModal', returnFocus);
	startRequest(root, jobId);
}

async function loadAiSubmissionDialog(root: Root, request: DialogRequest): Promise<void> {
	try {
		const api = getRuntime(root)?.api;
		if (!api?.getAiJobDetail) throw new Error('ai_job_detail_api_unavailable');
		const response = await api.getAiJobDetail(request.jobId, request.controller.signal);
		if (!isCurrent(root, request)) return;
		const job = response?.job ?? null;
		if (!job) {
			paintState(root, 'Submission not found', 'This AI Create submission is unavailable.');
			return;
		}
		const target = host(root);
		if (target) {
			target.innerHTML = renderAiSubmissionDetail(job as AiJobDetail);
			const title = target.querySelector<HTMLElement>('.community-detail-title');
			if (title) title.id = dialogTitleId;
			labelDialog(root);
		}
	} catch (error) {
		if (!isCurrent(root, request) || request.controller.signal.aborted) return;
		const message = error instanceof PrototypeApiError && error.status === 404 ? 'This AI Create submission is unavailable.' : 'Please try again.';
		paintState(root, 'Could not load submission', message, request.jobId);
	}
}

export function retryAiSubmissionDialog(root: Root, jobId: string): void {
	const modal = dialog(root);
	if (!modal?.classList.contains('active')) return;
	startRequest(root, jobId);
}

/** Cancels the active read before the shared modal primitive restores focus. */
export function closeAiSubmissionDialog(root: Root): void {
	requests.get(root as object)?.controller.abort();
	requests.delete(root as object);
	closeModal(root, 'aiSubmissionDetailModal');
}

/**
 * Hands the dialog's View Topic action to the normal Topic controller only
 * after its modal-history entry has been consumed, so a Topic child entry
 * never inherits `modalOpen` (same contract as the Community dialog).
 */
export function handoffAiSubmissionDialogTopic(root: Root, navigateToTopic: () => void): void {
	handoffModalForNavigation(root, 'aiSubmissionDetailModal', () => closeAiSubmissionDialog(root), navigateToTopic);
}
